import {Component, OnInit} from '@angular/core';
import {Invoice} from '../../model/invoice';
import {Invoice as ModernInvoice} from 'src/app/api/models';
import {InvoicesService} from 'src/app/api/services/invoices.service';
import {UntypedFormBuilder, UntypedFormGroup} from '@angular/forms';
import {Observable, combineLatest} from 'rxjs';
import {InvoicePeerService} from '../../service/invoice-peer.service';
import {map, tap} from 'rxjs/operators';
import {FinancingMethodsMapCache, InvoiceConfigurationsMapCache, PenaltyInterestMethodsMapCache} from '../../model/model-map-cache';
import {FinancingMethodId} from '../../model/financing-method';

@Component({
  selector: 'app-invoice-financing-widget',
  templateUrl: './invoice-financing-widget.component.html',
  styleUrls: ['./invoice-financing-widget.component.scss']
})
export class InvoiceFinancingWidgetComponent implements OnInit {

  invoice: Invoice;
  modernInvoice: ModernInvoice | null = null;
  form: UntypedFormGroup;

  invoice$: Observable<Invoice>;
  modernInvoice$: Observable<ModernInvoice>;
  maps$;

  FinancingMethodId = FinancingMethodId;

  edited = false;
  locked = false;

  constructor(
    private builder: UntypedFormBuilder,
    private invoicePeerService: InvoicePeerService,
    private invoicesService: InvoicesService,
    private invoiceConfigurationsMapCache: InvoiceConfigurationsMapCache,
    private financingMethodsMapCache: FinancingMethodsMapCache,
    private penaltyInterestMethodsMapCache: PenaltyInterestMethodsMapCache
  ) {
    this.form = this.builder.group({
      invoiceConfigurationId: [null],
      financingMethodId: [null],
      dailyInterest: [null],
      fixedInterest: [null],
      penaltyInterestMethodId: [null],
      guarantee: [null],
      dateFinanced: [null],
      amountFinanced: [null],
      lastPaymentDate: [null],
      finalPaymentDate: [null],
      finalPaymentAmount: [null],
      serviceFee: [null],
      subfinancingCost: [null],
      interestEndDateOverride: [null],
    });
  }

  edit(): void {
    this.invoicePeerService.lock();
    this.edited = true;
    this.form.patchValue(this.invoice);
    this.form.patchValue({
      interestEndDateOverride: this.modernInvoice?.interest_end_date_override
        ? new Date(this.modernInvoice.interest_end_date_override)
        : null,
    });
  }

  clearInterestEndDateOverride(): void {
    this.form.patchValue({ interestEndDateOverride: null });
    this.form.get('interestEndDateOverride').markAsDirty();
  }

  revert(): void {
    this.invoicePeerService.unlock();
    this.edited = false;
    this.form.reset();
  }

  save(): void {
    const invoice = this.invoice.clone();
    Object.assign(invoice, this.form.value);
    const overrideChanged = this.interestEndDateOverrideChanged();
    const overrideValue = this.form.value.interestEndDateOverride;
    const modernId = this.modernInvoice?.id;
    this.invoicePeerService
      .save(invoice)
      .subscribe(() => {
        if (overrideChanged && modernId !== undefined && modernId !== null) {
          this.invoicesService
            .setInvoiceInterestEndDateOverride({
              id: modernId,
              body: {
                interest_end_date_override: overrideValue
                  ? this.formatDateForApi(overrideValue)
                  : null,
              },
            })
            .subscribe(() => {
              this.edited = false;
              this.invoicePeerService.refresh();
            });
        } else {
          this.edited = false;
        }
      });
  }

  private interestEndDateOverrideChanged(): boolean {
    const formValue = this.form.value.interestEndDateOverride;
    const original = this.modernInvoice?.interest_end_date_override ?? null;
    const formIso = formValue ? this.formatDateForApi(formValue) : null;
    return formIso !== original;
  }

  private formatDateForApi(value: string | Date): string {
    const d = value instanceof Date ? value : new Date(value);
    const yyyy = d.getFullYear();
    const mm = String(d.getMonth() + 1).padStart(2, '0');
    const dd = String(d.getDate()).padStart(2, '0');
    return `${yyyy}-${mm}-${dd}`;
  }

  calculateInterests(): void {
    this.invoicePeerService.recalculate(this.invoice.invoiceId);
  }

  calculateInterestsLegacy(): void {
    this.invoicePeerService.calculateInterestsLegacy(this.invoice.invoiceId);
  }

  updateLastPaymentDateFromArex(): void {
    this.invoicePeerService.updateLastPaymentDateFromArex(this.invoice.invoiceId);
  }

  ngOnInit() {

    this.maps$ = combineLatest([
      this.invoiceConfigurationsMapCache.map$,
      this.financingMethodsMapCache.map$,
      this.penaltyInterestMethodsMapCache.map$
    ]).pipe(
      map(([configurations, financingMethods, penaltyInterestMethods]) => ({configurations, financingMethods, penaltyInterestMethods}))
    );

    this.invoice$ = this.invoicePeerService
      .get()
      .pipe(
        tap(invoice => {
            this.invoice = invoice;
          }
        ));

    this.modernInvoice$ = this.invoicePeerService
      .getInvoice()
      .pipe(
        tap(modernInvoice => {
            this.modernInvoice = modernInvoice;
          }
        ));

    this.invoicePeerService.getState().subscribe((state) => {
      if (state === 'locked') {
        this.locked = true;
      } else if (state === 'unlocked') {
        this.locked = false;
      }
    });
  }
}
